import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Drawer,
  Grid,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Divider,
} from '@mui/material';
import { Search, Visibility, MoreVert } from '@mui/icons-material';
import QRCode from 'react-qrcode-logo';

interface EquipmentFormData {
  id: string;
  type: 'Wyposażenie biura' | 'Sprzęt budowlany' | 'Inne' | '';
  name: string;
  description: string;
  serialNumber: string;
  netValue: string;
  notes: string;
  photo: File | null;
}

const EquipmentPage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [nextId, setNextId] = useState(6);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState<EquipmentFormData>({
    id: '',
    type: '',
    name: '',
    description: '',
    serialNumber: '',
    netValue: '',
    notes: '',
    photo: null,
  });
  
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof EquipmentFormData, string>>>({});
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  // Funkcja formatująca ID
  const formatEquipmentId = (id: number): string => {
    return `ST-${String(id).padStart(4, '0')}`;
  };

  // Przykładowe dane - później będzie pobierane z API
  const equipmentData = [
    { id: 1, name: 'Wiertarka Bosch', description: 'Wiertarka udarowa 18V', serialNumber: 'BOS-12345', netValue: 1250.50, notes: 'Wymaga konserwacji', category: 'Sprzęt budowlany' },
    { id: 2, name: 'Szczotka do malowania', description: 'Szczotka 10cm', serialNumber: 'SCH-67890', netValue: 45.00, notes: '', category: 'Sprzęt budowlany' },
    { id: 3, name: 'Drukarka HP', description: 'Drukarka laserowa', serialNumber: 'HP-11111', netValue: 890.00, notes: '', category: 'Wyposażenie biura' },
    { id: 4, name: 'Monitor Dell', description: 'Monitor 24 cali', serialNumber: 'DEL-22222', netValue: 650.00, notes: '', category: 'Wyposażenie biura' },
    { id: 5, name: 'Inny przedmiot', description: 'Opis', serialNumber: 'OTH-33333', netValue: 100.00, notes: '', category: 'Inne' },
  ];

  const categories = ['Wszystkie', 'Wyposażenie biura', 'Sprzęt budowlany', 'Inne'];
  
  const getFilteredByCategory = (category: string) => {
    if (category === 'Wszystkie') return equipmentData;
    return equipmentData.filter(item => item.category === category);
  };

  const getFilteredData = () => {
    const categoryFiltered = getFilteredByCategory(categories[tabValue]);
    return categoryFiltered.filter(item =>
      searchText === '' ||
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description.toLowerCase().includes(searchText.toLowerCase()) ||
      item.serialNumber.toLowerCase().includes(searchText.toLowerCase()) ||
      item.notes.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const filteredData = getFilteredData();
  
  const tabCounts = categories.map(cat => {
    if (cat === 'Wszystkie') return equipmentData.length;
    return equipmentData.filter(item => item.category === cat).length;
  });

  const handleOpenAddDialog = () => {
    setAddDialogOpen(true);
    setFormData({
      id: formatEquipmentId(nextId),
      type: '',
      name: '',
      description: '',
      serialNumber: '',
      netValue: '',
      notes: '',
      photo: null,
    });
    setFormErrors({});
    setPhotoPreview(null);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setFormData({
      id: '',
      type: '',
      name: '',
      description: '',
      serialNumber: '',
      netValue: '',
      notes: '',
      photo: null,
    });
    setFormErrors({});
    setPhotoPreview(null);
  };

  const handleFormChange = (field: keyof EquipmentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setFormData(prev => ({ ...prev, photo: file }));
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setFormErrors(prev => ({ ...prev, photo: 'Proszę wybrać plik obrazu' }));
      }
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof EquipmentFormData, string>> = {};
    
    if (!formData.id.trim()) errors.id = 'ID jest wymagane';
    if (!formData.type) errors.type = 'Typ jest wymagany';
    if (!formData.name.trim()) errors.name = 'Nazwa jest wymagana';
    if (!formData.serialNumber.trim()) errors.serialNumber = 'Nr seryjny jest wymagany';
    if (formData.netValue && isNaN(parseFloat(formData.netValue))) {
      errors.netValue = 'Wartość netto musi być liczbą';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    
    // TODO: Tutaj będzie zapis do API
    // Wyciągnij numer z ID (np. ST-0001 -> 1)
    const idMatch = formData.id.match(/ST-(\d+)/);
    const numericId = idMatch ? parseInt(idMatch[1], 10) : nextId;
    
    const newEquipment = {
      id: numericId,
      name: formData.name,
      description: formData.description,
      serialNumber: formData.serialNumber,
      netValue: parseFloat(formData.netValue) || 0,
      notes: formData.notes,
      category: formData.type || 'Inne',
    };
    
    // equipmentData.push(newEquipment); // W rzeczywistości będzie zapis do API
    setNextId(prev => Math.max(prev + 1, numericId + 1));
    handleCloseAddDialog();
  };

  const handleDelete = () => {
    // TODO: Implementacja usuwania wyposażenia
    handleCloseAddDialog();
  };

  const generateSTL = () => {
    if (!formData.id) {
      alert('Proszę najpierw wprowadzić ID wyposażenia');
      return;
    }

    // Poczekaj chwilę, aby upewnić się, że QRCode został wyrenderowany
    setTimeout(() => {
      const qrCanvas = qrRef.current?.querySelector('canvas');
      if (!qrCanvas) {
        console.error('Nie znaleziono canvas z kodem QR. Sprawdzam strukturę DOM:', qrRef.current);
        alert('Nie można znaleźć kodu QR. Upewnij się, że ID zostało wprowadzone i kod QR jest widoczny.');
        return;
      }

    // Utwórz canvas z logo i QR (tak jak w QRGeneratorPage)
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;

    const qrSize = qrCanvas.width;
    const quietZone = 10;
    const qrActualSize = qrSize - (quietZone * 2);
    const padding = 20;
    const spacing = 15;
    const borderRadius = 0;

    // Oblicz rozmiar logo
    tempCtx.font = "600 18px 'Roboto', sans-serif";
    const logoText = 'UNITRESCO';
    const testTextMetrics = tempCtx.measureText(logoText);
    const testFontSize = 18;
    const targetWidth = qrActualSize;
    const fontSize = Math.floor((testFontSize * targetWidth) / testTextMetrics.width);
    tempCtx.font = `600 ${fontSize}px 'Roboto', sans-serif`;
    const finalTextMetrics = tempCtx.measureText(logoText);
    const logoHeight = finalTextMetrics.actualBoundingBoxAscent + finalTextMetrics.actualBoundingBoxDescent;

    const topMargin = Math.max(finalTextMetrics.fontBoundingBoxAscent - finalTextMetrics.actualBoundingBoxAscent, 4);
    const contentHeight = logoHeight + spacing + qrSize;
    const totalCanvasHeight = padding + logoHeight + spacing + qrSize + padding - topMargin;
    const verticalPadding = (totalCanvasHeight - contentHeight) / 2;
    const topPadding = verticalPadding;
    const bottomPadding = verticalPadding;

    // Utwórz pełny obraz w wysokiej rozdzielczości
    const highResScale = 2;
    tempCanvas.width = (qrSize + (padding * 2)) * highResScale;
    tempCanvas.height = totalCanvasHeight * highResScale;
    tempCtx.scale(highResScale, highResScale);

    // Rysuj białe tło
    tempCtx.fillStyle = '#FFFFFF';
    tempCtx.fillRect(0, 0, tempCanvas.width / highResScale, tempCanvas.height / highResScale);

    // Rysuj logo
    tempCtx.fillStyle = '#c53030';
    tempCtx.textAlign = 'center';
    tempCtx.textBaseline = 'alphabetic';
    tempCtx.font = `600 ${fontSize}px 'Roboto', sans-serif`;
    const logoY = topPadding + topMargin + finalTextMetrics.fontBoundingBoxAscent;
    tempCtx.fillText(logoText, tempCanvas.width / highResScale / 2, logoY);

    // Rysuj QR code
    tempCtx.drawImage(qrCanvas, padding, topPadding + topMargin + logoHeight + spacing);

    // Parametry dla druku 3D
    const baseHeightMM = 1.0;
    const qrHeightMM = 0.4;
    const logoHeightMM = 0.4;
    const targetPlateWidthMM = 40.0;
    const scaleFactor = targetPlateWidthMM / (qrSize + (padding * 2));
    const pixelSizeMM = scaleFactor / highResScale;

    // Pobierz dane obrazu
    const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    const data = imageData.data;
    const width = tempCanvas.width;
    const height = tempCanvas.height;

    let stlContent = '';
    const plateTriangles: string[] = [];
    const qrTriangles: string[] = [];
    const logoTriangles: string[] = [];

    const addTriangle = (v1: number[], v2: number[], v3: number[], normal: number[], target: string[]) => {
      const triangle = `  facet normal ${normal[0].toFixed(6)} ${normal[1].toFixed(6)} ${normal[2].toFixed(6)}\n` +
        `    outer loop\n` +
        `      vertex ${v1[0].toFixed(6)} ${v1[1].toFixed(6)} ${v1[2].toFixed(6)}\n` +
        `      vertex ${v2[0].toFixed(6)} ${v2[1].toFixed(6)} ${v2[2].toFixed(6)}\n` +
        `      vertex ${v3[0].toFixed(6)} ${v3[1].toFixed(6)} ${v3[2].toFixed(6)}\n` +
        `    endloop\n` +
        `  endfacet\n`;
      target.push(triangle);
    };

    // Generuj podstawę
    const plateWidthMM = (qrSize + (padding * 2)) * scaleFactor;
    const plateHeightMM = (padding + logoHeight + spacing + qrSize + padding) * scaleFactor;

    const addPlateTriangle = (v1: number[], v2: number[], v3: number[], normal: number[]) => {
      addTriangle(v1, v2, v3, normal, plateTriangles);
    };

    // Dolna powierzchnia podstawy
    addPlateTriangle([0, 0, 0], [plateWidthMM, 0, 0], [plateWidthMM, plateHeightMM, 0], [0, 0, -1]);
    addPlateTriangle([0, 0, 0], [plateWidthMM, plateHeightMM, 0], [0, plateHeightMM, 0], [0, 0, -1]);
    
    // Boki podstawy
    addPlateTriangle([0, 0, 0], [0, 0, baseHeightMM], [plateWidthMM, 0, baseHeightMM], [0, -1, 0]);
    addPlateTriangle([0, 0, 0], [plateWidthMM, 0, baseHeightMM], [plateWidthMM, 0, 0], [0, -1, 0]);
    addPlateTriangle([plateWidthMM, 0, 0], [plateWidthMM, 0, baseHeightMM], [plateWidthMM, plateHeightMM, baseHeightMM], [1, 0, 0]);
    addPlateTriangle([plateWidthMM, 0, 0], [plateWidthMM, plateHeightMM, baseHeightMM], [plateWidthMM, plateHeightMM, 0], [1, 0, 0]);
    addPlateTriangle([plateWidthMM, plateHeightMM, 0], [plateWidthMM, plateHeightMM, baseHeightMM], [0, plateHeightMM, baseHeightMM], [0, 1, 0]);
    addPlateTriangle([plateWidthMM, plateHeightMM, 0], [0, plateHeightMM, baseHeightMM], [0, plateHeightMM, 0], [0, 1, 0]);
    addPlateTriangle([0, plateHeightMM, 0], [0, plateHeightMM, baseHeightMM], [0, 0, baseHeightMM], [-1, 0, 0]);
    addPlateTriangle([0, plateHeightMM, 0], [0, 0, baseHeightMM], [0, 0, 0], [-1, 0, 0]);
    
    // Górna powierzchnia podstawy
    addPlateTriangle([0, 0, baseHeightMM], [plateWidthMM, plateHeightMM, baseHeightMM], [plateWidthMM, 0, baseHeightMM], [0, 0, 1]);
    addPlateTriangle([0, 0, baseHeightMM], [0, plateHeightMM, baseHeightMM], [plateWidthMM, plateHeightMM, baseHeightMM], [0, 0, 1]);

    // Generuj logo i QR code jako wypukłe nad podstawą
    const extrudeZ = baseHeightMM;
    const sampleStep = 2;
    const sampledPixelSize = pixelSizeMM * sampleStep;

    const logoStartY = Math.max(0, (logoY - finalTextMetrics.fontBoundingBoxAscent) * highResScale);
    const logoEndY = (logoY + finalTextMetrics.fontBoundingBoxDescent) * highResScale;
    const qrStartY = (topPadding + topMargin + logoHeight + spacing) * highResScale;
    const qrEndY = (topPadding + topMargin + logoHeight + spacing + qrSize) * highResScale;

    const addExtrudedBlock = (px: number, py: number, pz: number, blockSize: number, blockHeight: number, targetTriangles: string[]) => {
      const topZ = pz + blockHeight;
      
      addTriangle([px, py, topZ], [px + blockSize, py, topZ], [px + blockSize, py + blockSize, topZ], [0, 0, 1], targetTriangles);
      addTriangle([px, py, topZ], [px + blockSize, py + blockSize, topZ], [px, py + blockSize, topZ], [0, 0, 1], targetTriangles);

      addTriangle([px, py, pz], [px, py + blockSize, topZ], [px, py + blockSize, pz], [-1, 0, 0], targetTriangles);
      addTriangle([px, py, pz], [px, py, topZ], [px, py + blockSize, topZ], [-1, 0, 0], targetTriangles);
      
      addTriangle([px + blockSize, py, pz], [px + blockSize, py + blockSize, pz], [px + blockSize, py + blockSize, topZ], [1, 0, 0], targetTriangles);
      addTriangle([px + blockSize, py, pz], [px + blockSize, py + blockSize, topZ], [px + blockSize, py, topZ], [1, 0, 0], targetTriangles);
      
      addTriangle([px, py, pz], [px + blockSize, py, topZ], [px, py, topZ], [0, -1, 0], targetTriangles);
      addTriangle([px, py, pz], [px + blockSize, py, pz], [px + blockSize, py, topZ], [0, -1, 0], targetTriangles);
      
      addTriangle([px, py + blockSize, pz], [px, py + blockSize, topZ], [px + blockSize, py + blockSize, topZ], [0, 1, 0], targetTriangles);
      addTriangle([px, py + blockSize, pz], [px + blockSize, py + blockSize, topZ], [px + blockSize, py + blockSize, pz], [0, 1, 0], targetTriangles);
    };

    for (let y = 0; y < height; y += sampleStep) {
      for (let x = 0; x < width; x += sampleStep) {
        let isLogoPixel = false;
        let isQRPixel = false;
        
        const isInLogoArea = y >= logoStartY && y < logoEndY;
        const isInQRArea = y >= qrStartY && y < qrEndY;
        
        if (!isInLogoArea && !isInQRArea) continue;
        
        for (let dy = 0; dy < sampleStep && y + dy < height; dy++) {
          for (let dx = 0; dx < sampleStep && x + dx < width; dx++) {
            const idx = ((y + dy) * width + (x + dx)) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            const alpha = data[idx + 3];
            
            if (alpha > 128) {
              const avg = (r + g + b) / 3;
              
              if (isInLogoArea) {
                if (avg < 250) {
                  isLogoPixel = true;
                  break;
                }
              }
              
              if (!isLogoPixel && isInQRArea && !isInLogoArea) {
                if (r < 100 && g < 100 && b < 100 && avg < 80) {
                  isQRPixel = true;
                }
              }
            }
          }
          if (isLogoPixel) break;
        }

        if (isLogoPixel || isQRPixel) {
          const originalX = x * pixelSizeMM;
          const originalY = y * pixelSizeMM;
          const px = originalX;
          let py = plateHeightMM - originalY - sampledPixelSize;
          
          const blockSize = sampledPixelSize;
          const pz = extrudeZ;

          if (isLogoPixel) {
            addExtrudedBlock(px, py, pz, blockSize, logoHeightMM, logoTriangles);
          } else if (isQRPixel) {
            addExtrudedBlock(px, py, pz, blockSize, qrHeightMM, qrTriangles);
          }
        }
      }
    }

    // Połącz wszystkie sekcje w jeden plik STL
    stlContent += 'solid Plate_White\n';
    stlContent += plateTriangles.join('');
    stlContent += 'endsolid Plate_White\n';

    stlContent += 'solid QR_Black\n';
    stlContent += qrTriangles.join('');
    stlContent += 'endsolid QR_Black\n';

    stlContent += 'solid Logo_Red\n';
    stlContent += logoTriangles.join('');
    stlContent += 'endsolid Logo_Red\n';

      // Pobierz plik STL
      const blob = new Blob([stlContent], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `equipment-${formData.id}-3d.stl`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  // Style TextField zgodne z oknem dodawania oferty
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#2D2D32'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: '4px',
      '&:not(.MuiInputBase-multiline)': {
        height: 32
      }
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiInputBase-input': {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32',
      padding: '8px 12px',
      boxSizing: 'border-box'
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#B9B9B9',
      opacity: 1
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      marginLeft: 0,
      marginTop: '4px',
      color: '#d32f2f'
    }
  } as const;

  // Style Select zgodne z oknem dodawania oferty
  const selectSx = {
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '&:has(.MuiSelect-root[aria-expanded="true"]) .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiSelect-select': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    }
  } as const;

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      {/* Nagłówek i przycisk Dodaj */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0,
            mt: '8.5px'
          }}
        >
          Wyposażenie
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Button 
            variant="contained" 
            onClick={handleOpenAddDialog}
            data-action="add"
            sx={{ 
              width: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              wordBreak: 'keep-all',
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' }
            }}
          >
            Dodaj
          </Button>
        </Box>
      </Box>

      {/* Filtr */}
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: '300px',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        <TextField
          size="small"
          placeholder="Szukaj w wyposażeniu"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          inputProps={{ 
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                try { (e.currentTarget as HTMLInputElement).blur(); } catch {}
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{ 
            width: 300,
            mt: '20px',
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: '#fff',
              borderRadius: '4px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d4d4d9'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966'
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966 !important'
            },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />
      </Box>

      {/* Zakładki */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, v) => setTabValue(v)} 
          aria-label="equipment tabs"
          sx={{ 
            minHeight: 40,
            height: 40,
            flexGrow: 1,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: '2px' }
          }}
        >
          {categories.map((category, index) => (
            <Tab 
              key={category}
              disableRipple
              label={`${category} (${tabCounts[index]})`}
              sx={{
                textTransform: 'none',
                color: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                px: 3,
                minHeight: 32,
                height: 32,
                mt: '8px',
                bgcolor: '#F1F1F6',
                borderRadius: '4px 4px 0 0',
                '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tabela */}
      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        sx={{
          height: 432,
          overflow: 'auto',
          position: 'relative',
          border: '1px solid #d0d0d6',
          borderBottom: '1px solid #d0d0d6',
          borderRadius: 0,
          borderTopRightRadius: 4,
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          overflowX: 'hidden',
          boxShadow: 'none',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { width: 0, height: 0 }
        }}
      >
        <Table size="small" stickyHeader sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
          '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>ID</TableCell>
              <TableCell sx={{ width: 200 }}>Nazwa</TableCell>
              <TableCell sx={{ width: 250 }}>Opis</TableCell>
              <TableCell sx={{ width: 150 }}>Nr seryjny</TableCell>
              <TableCell sx={{ width: 120 }} align="right">Wartość netto</TableCell>
              <TableCell>Uwagi</TableCell>
              <TableCell align="center" sx={{ width: 60 }}></TableCell>
              <TableCell align="right" sx={{ width: 60 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                  Brak danych
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item) => (
                <TableRow key={item.id} hover sx={{
                  '&:hover': { backgroundColor: '#F5F5FF !important' },
                  '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' },
                }}>
                  <TableCell>{formatEquipmentId(item.id)}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.serialNumber}</TableCell>
                  <TableCell align="right">{item.netValue ? new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', minimumFractionDigits: 2 }).format(item.netValue) : '—'}</TableCell>
                  <TableCell>{item.notes || '—'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        // TODO: Implementacja podglądu
                      }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        // TODO: Implementacja menu
                      }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Drawer dodawania wyposażenia */}
      <Drawer
        anchor="right"
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
              Dodaj wyposażenie
            </Typography>
            <IconButton 
              onClick={handleCloseAddDialog} 
              size="small"
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 30,
                minHeight: 30,
                '& > *': {
                  pointerEvents: 'none',
                },
              }}
            >
              <Box 
                component="img" 
                src="/assets/close.svg" 
                alt="Zamknij" 
                sx={{ 
                  width: 13, 
                  height: 13, 
                  display: 'block',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                }} 
              />
            </IconButton>
          </Box>
          
          <Box sx={{ flex: 1, overflow: 'auto', px: 4, py: 3 }}>
            <Grid container rowSpacing={1.5} columnSpacing={3}>
            <Grid item xs={12} sm={6}>
              <FieldLabel text="ID" required />
              <TextField
                fullWidth
                size="small"
                value={formData.id}
                onChange={(e) => handleFormChange('id', e.target.value)}
                error={!!formErrors.id}
                helperText={formErrors.id}
                sx={textFieldSx}
                placeholder="ST-0001"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FieldLabel text="Typ" required />
              <FormControl fullWidth size="small" sx={selectSx} error={!!formErrors.type}>
                <Select
                  value={formData.type}
                  onChange={(e) => handleFormChange('type', e.target.value)}
                  displayEmpty
                  renderValue={(val) => val || 'Wybierz z listy'}
                  IconComponent={(iconProps: any) => (
                    <Box
                      component="img"
                      alt="dropdown"
                      src="/assets/icons/keyboard_arrow_down.svg"
                      {...iconProps}
                      sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                    />
                  )}
                  sx={{
                    '& .MuiSelect-select': {
                      color: formData.type ? 'inherit' : '#B9B9B9'
                    },
                    '&[aria-expanded="true"]': {
                      '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                    },
                    '&.Mui-expanded': {
                      '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                    }
                  }}
                >
                  <MenuItem value="Wyposażenie biura">Wyposażenie biura</MenuItem>
                  <MenuItem value="Sprzęt budowlany">Sprzęt budowlany</MenuItem>
                  <MenuItem value="Inne">Inne</MenuItem>
                </Select>
                {formErrors.type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.type}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FieldLabel text="Nazwa" required />
              <TextField
                fullWidth
                size="small"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                sx={textFieldSx}
                placeholder="Nazwa"
              />
            </Grid>

            <Grid item xs={12}>
              <FieldLabel text="Opis" />
              <TextField
                fullWidth
                size="small"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={3}
                sx={textFieldSx}
                placeholder="Opis"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FieldLabel text="Nr seryjny" required />
              <TextField
                fullWidth
                size="small"
                value={formData.serialNumber}
                onChange={(e) => handleFormChange('serialNumber', e.target.value)}
                error={!!formErrors.serialNumber}
                helperText={formErrors.serialNumber}
                sx={textFieldSx}
                placeholder="Nr seryjny"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FieldLabel text="Wartość netto" />
              <TextField
                fullWidth
                size="small"
                value={formData.netValue}
                onChange={(e) => handleFormChange('netValue', e.target.value)}
                error={!!formErrors.netValue}
                helperText={formErrors.netValue}
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">PLN</InputAdornment>
                }}
                sx={textFieldSx}
                placeholder="0.00"
              />
            </Grid>

            <Grid item xs={12}>
              <FieldLabel text="Uwagi" />
              <TextField
                fullWidth
                size="small"
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                multiline
                rows={2}
                sx={textFieldSx}
                placeholder="Uwagi"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ border: '1px dashed #d4d4d9', borderRadius: 1, p: 2, textAlign: 'center', minHeight: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoChange}
                />
                {photoPreview ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      component="img"
                      src={photoPreview}
                      alt="Podgląd"
                      sx={{ maxWidth: '100%', maxHeight: 150, mb: 1 }}
                    />
                    <Button
                      size="small"
                      onClick={() => {
                        setPhotoPreview(null);
                        setFormData(prev => ({ ...prev, photo: null }));
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      Usuń zdjęcie
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      component="img"
                      src="/assets/icons/upload_equipment.svg"
                      alt="Upload"
                      sx={{ 
                        width: 48, 
                        height: 48, 
                        mb: 1,
                        color: '#595966',
                        filter: 'brightness(0) saturate(100%) invert(0.4)'
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Dodaj zdjęcie
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Wybierz plik
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ border: '1px solid #d4d4d9', borderRadius: 1, p: 2, textAlign: 'center', minHeight: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Kod QR
                </Typography>
                {formData.id ? (
                  <Box ref={qrRef} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1, display: 'inline-block' }}>
                      <QRCode
                        value={formData.id}
                        size={150}
                        ecLevel="M"
                        quietZone={10}
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                        qrStyle="squares"
                        eyeRadius={0}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      ID: {formData.id}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Kod QR zostanie wygenerowany po zapisaniu
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
          </Box>
          
          <Divider />
          <Box sx={{ px: 4, py: 3, borderTop: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button 
              onClick={handleDelete}
              sx={{
                textTransform: 'none',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                color: '#d32f2f',
                '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' }
              }}
            >
              Usuń
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                onClick={generateSTL}
                disabled={!formData.id}
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#2D2D32',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                  '&:disabled': { color: '#B9B9B9' }
                }}
              >
                Pobierz STL
              </Button>
              <Button 
                onClick={handleCloseAddDialog}
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#2D2D32',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                }}
              >
                Anuluj
              </Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  backgroundColor: '#4040DB',
                  '&:hover': { backgroundColor: '#1F1FB0' }
                }}
              >
                Dodaj
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default EquipmentPage;
